import { Injectable, inject , signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccountService } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { WebviewService } from '../platform/webview.service';
import { ECar, ECarSettings } from '../../models/ecar';
import { ECarType } from '../../models/ecar-type';
import { VEHICLE_IMAGE_URL } from '../core/http.service';
import { DisplayMode } from '../../models/display-mode';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { WelcomeComponent } from '../../components/modals/info/welcome/welcome.component';
import { VehicleOptionsDialogComponent } from '../../components/modals/vehicle-options-dialog/vehicle-options-dialog.component';
import { WidgetService } from '../platform/widget.service';

@Injectable({ providedIn: 'root' })
export class VehicleService {

  public ECars: ECarType[] = [];
  public CarTypes: { name: string, subtypes: string[] }[] = [];
  public ObservableECarsLoaded = new BehaviorSubject<{ ECars: ECarType[], CarTypes: { name: string, subtypes: string[] }[] } | null>(null);

  private SelectedCar: ECarType = new ECarType(-1, null!, null!, null!, null!, null!, null!, null!, null!, null!, null!, null!, '', null!, null!, null!, '', '');
  /** Signal-backed: consumers read it via getSelectedCarIcon() and re-render when the skin changes. */
  private readonly SelectedCarIcon = signal<string>('');
  public ObservableSelectedCar = new BehaviorSubject<ECar | null>(null);

  private ChargingStations: boolean[] = [];
  public ObservableChargingStations = new BehaviorSubject<boolean[]>([]);

  private ECarSettings: ECarSettings | null = null;
  public ObservableECarSettings = new BehaviorSubject<ECarSettings | null>(null);

  public readonly ObservableBatteryChanged = new BehaviorSubject<number | null>(null);
  public readonly ObservableTripRecordingChanged = new BehaviorSubject<number>(1);


  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly webviewService = inject(WebviewService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
      .replace('$skin', this.settingsService.getMap().Skin)
      .replace('$icon_name', 'car_icon_default'));

    this.accountService.ObservableSelectedUserCar.subscribe((resp: ECar | null) => {
      if (resp?.ECarType) {
        this.ObservableSelectedCar.next(resp);
        this.setCarIcon(resp.ECarType);

        const chargingStations = [false, false, false, false, false, false];
        for (let i = 0; i < resp.ECarType.ChargerTypes.length; i++) {
          chargingStations[resp.ECarType.ChargerTypes[i] - 1] = true;
        }
        this.setChargingStations(chargingStations);

        if (resp.settings) {
          if (this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE) {
            resp.settings.batterySafetyLimit = resp.settings.batterySafetyLimitRoute;
          } else {
            resp.settings.batterySafetyLimit = resp.settings.batterySafetyLimitRha;
          }
          this.setECarSettings(resp.settings);
          if (resp.settings.tripRecording != undefined) {
            this.ObservableTripRecordingChanged.next(resp.settings.tripRecording);
          }
        }
      }
    });

    this.accountService.ObservableIsAuthorized.subscribe((resp) => {
      if (resp === false) {
        this.initCar();
      }
    });

    this.settingsService.ObservableMap.subscribe((resp) => {
      if (resp && this.SelectedCar) {
        this.setCarIcon(this.getSelectedCar());
      }
    });

    this.ObservableBatteryChanged.subscribe((resp) => {
      if (this.accountService.getIsAuthorized() && resp) {
        const userCar = this.accountService.user.getSelectedUserCar();
        if (userCar && userCar.settings.batteryLevel != resp) {
          userCar.settings.batteryLevel = resp;
          this.accountService.editVehicle(userCar.userVehicleID, null, null, null, JSON.stringify(userCar.settings), false).subscribe();
        }
      }
      if (!this.accountService.getIsAuthorized() && resp) {
        localStorage.setItem('batteryLevel', resp.toString());
      }
    });

    this.initWebViewSetBattery();
  }

  public initAllVehicles(allVehicles: ECarType[]): void {
    this.ECars = allVehicles;
    this.CarTypes = this.utilsService.collectCarTypes(this.ECars);
    this.initCar();
    this.ObservableECarsLoaded.next({ ECars: this.ECars, CarTypes: this.CarTypes });
  }

  public initCar(): void {
    let selectedCarId: string | null = null;
    let selectedCarName: string | null = null; // legacy check for older non-auth users
    let selectedCar: ECarType | null = null;

    if (this.widgetService.isWidget()) {
      // Widget mode: no welcome / car-selector dialogs. The car comes from `carid`,
      // then from the stored selection, then the default.
      const widgetCarId = this.widgetService.getCarParams().carId;
      selectedCar = widgetCarId != null ? this.getCarByID(widgetCarId) ?? null : null;
      if (!selectedCar && this.settingsService.acceptCookies) {
        selectedCarId = localStorage.getItem('selectedCarId');
        selectedCar = selectedCarId ? this.ECars.find((ecar) => ecar.Id === parseInt(selectedCarId!)) ?? null : null;
      }
      if (selectedCar) {
        this.setSelectedCar(selectedCar, false);
      } else {
        this.selectDefaultCar();
      }
    }
    else if (this.settingsService.acceptCookies) {
      selectedCarName = localStorage.getItem('selectedCar');
      selectedCarId = localStorage.getItem('selectedCarId');

      if (selectedCarName == null && selectedCarId == null) {
        if (!document.URL.includes('carid')) {
          this.stackedModalsService.openModal(WelcomeComponent, {
            autoFocus: false,
            panelClass: ['dialog-snap-to-fullscreen'],
            data: { firstInit: true },
          });
          this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
            data: {},
            autoFocus: false,
            panelClass: ['car-selector-outer', 'dialog-snap-to-fullscreen'],
          });
        }
        this.selectDefaultCar();
      } else if (selectedCarId) {
        selectedCar = this.ECars.find((ecar) => ecar.Id === parseInt(selectedCarId!)) ?? null;
        if (selectedCar) {
          this.setSelectedCar(selectedCar, false);
        } else {
          this.selectDefaultCar();
          this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
            data: {},
            autoFocus: false,
            panelClass: ['car-selector-outer', 'dialog-snap-to-fullscreen'],
          });
        }
      } else {
        this.selectDefaultCar();
        this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
          data: {},
          autoFocus: false,
          panelClass: ['car-selector-outer', 'dialog-snap-to-fullscreen'],
        });
      }
    }

    let chargingStations: boolean[] = [];
    let chargingStationsStorage: string | null = null;
    if (this.settingsService.acceptCookies) {
      chargingStationsStorage = localStorage.getItem('chargingStations');
    }

    const widgetChargerTypes = this.widgetService.isWidget() ? this.widgetService.getCarParams().chargerTypes : null;
    if (widgetChargerTypes) {
      chargingStations = widgetChargerTypes;
    }
    else if (chargingStationsStorage) {
      const csArray = chargingStationsStorage.split(',');
      for (let i = 0; i < csArray.length; i++) {
        if (csArray[i] === 'true') chargingStations.push(true);
        if (csArray[i] === 'false') chargingStations.push(false);
      }
    } else {
      chargingStations = [false, false, false, false, false, false];
      for (let i = 0; i < this.SelectedCar.ChargerTypes.length; i++) {
        chargingStations[this.SelectedCar.ChargerTypes[i] - 1] = true;
      }
    }

    this.setChargingStations(chargingStations);

    const ecar: ECarType = this.getSelectedCar();
    if (ecar) {
      ecar.ChargerTypes = [];
      for (let i = 0; i < chargingStations.length; i++) {
        if (chargingStations[i]) ecar.ChargerTypes.push(i + 1);
      }
      this.setSelectedCar(ecar, true);
    }

    this.applyWidgetCarSettings();
  }

  /**
   * Widget mode: merge the car parameters given in the URL into the current
   * ECarSettings and emit. Idempotent — called after initCar() and again once the
   * vehicle panel has restored its own stored values, so the URL wins either way.
   */
  public applyWidgetCarSettings(): void {
    if (!this.widgetService.isWidget()) return;
    const overrides = this.widgetService.getCarParams().settings;
    if (Object.keys(overrides).length === 0) return;

    const base: ECarSettings = this.ECarSettings ?? {
      batteryLevel: 73, weight: 2, tirePressure: 2.3, userBehavior: 50, speedLimiter: 100,
      batterySafetyLimitRha: 50, batterySafetyLimitRoute: 15, minBatteryAtDestination: 10,
    };
    const merged: ECarSettings = { ...base, ...overrides };
    merged.batterySafetyLimit = this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE
      ? merged.batterySafetyLimitRoute : merged.batterySafetyLimitRha;
    this.setECarSettings(merged);
  }

  public selectDefaultCar(): void {
    const defCarId: number = this.utilsService.defaultCarIndex;
    const defCar: ECarType | undefined = this.ECars.find((ecar) => ecar.Id === defCarId);
    if (defCar) {
      this.setSelectedCar(defCar, false);
    }
  }

  public getCarByID(id: number): ECarType | undefined {
    return this.ECars.find((ecar) => ecar.Id === id);
  }

  public setSelectedCar(ecar: ECarType, setCarChargingStations: boolean): void {
    this.SelectedCar = new ECarType(
      ecar.Id, ecar.CarWeight, ecar.ChargerTypes, ecar.DesignCapacity,
      ecar.DragCoefficient, ecar.DragCross, ecar.Name, ecar.Range,
      ecar.FactoryRange, ecar.FactoryRangeSource, ecar.TopSpeed,
      ecar.TotalPower, ecar.Icon, ecar.ChargePower, ecar.FastChargePower,
      ecar.Type, ecar.Subtype, ecar.GreenCarsLink
    );

    if (ecar.Icon) {
      this.setCarIcon(ecar);
    }

    if (setCarChargingStations) {
      const chargingStations = [false, false, false, false, false, false];
      for (let i = 0; i < this.SelectedCar.ChargerTypes.length; i++) {
        chargingStations[this.SelectedCar.ChargerTypes[i] - 1] = true;
      }
      this.setChargingStations(chargingStations);
    }

    if (this.settingsService.acceptCookies) {
      localStorage.setItem('selectedCarId', this.SelectedCar.Id.toString());
    }

    const nullUserEcar = new ECar();
    nullUserEcar.ECarType = ecar;
    nullUserEcar.name = '';
    nullUserEcar.selected = true;
    nullUserEcar.userVehicleID = -1;
    this.ObservableSelectedCar.next(nullUserEcar);
  }

  public getSelectedCar(): ECarType {
    if (this.accountService.getIsAuthorized() && this.accountService.user.getSelectedCar()) {
      return this.accountService.user.getSelectedCar()!;
    }
    return this.SelectedCar;
  }

  public getSelectedCarIcon(): string {
    return this.SelectedCarIcon();
  }

  public setChargingStations(params: boolean[]): void {
    this.ChargingStations = params;
    this.ObservableChargingStations.next(params);
  }

  public getChargingStations(): boolean[] {
    return this.ChargingStations;
  }

  private setCarIcon(ecar: ECarType): void {
    if (ecar.Icon.length > 0) {
      this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
        .replace('$skin', this.settingsService.getMap().Skin)
        .replace('$icon_name', ecar.Icon));
    } else {
      this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
        .replace('$skin', this.settingsService.getMap().Skin)
        .replace('$icon_name', 'car_icon_default'));
    }
  }

  public getECarSettings(): ECarSettings | null { return this.ECarSettings; }

  public setECarSettings(params: ECarSettings): void {
    this.ECarSettings = params;
    this.ObservableECarSettings.next(params);
  }

  public getBattery(): number { 
    return this.ECarSettings?.batteryLevel ?? 76; 
  }

  public setBattery(batteryLevel: number): void {
    if (batteryLevel && this.ECarSettings) {
      this.ECarSettings.batteryLevel = batteryLevel;
      this.ecarSettingsUpdate();
    }
  }

  // Updates battery level in ECarSettings without emitting ObservableECarSettings,
  // so the change does not trigger a new calculation. Use when syncing back a
  // server-returned SOC value for display purposes only.
  public setBatterySilent(batteryLevel: number): void {
    if (batteryLevel && this.ECarSettings) {
      this.ECarSettings.batteryLevel = batteryLevel;
    }
  }

  public setWeight(weight: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.weight = weight;
      this.ecarSettingsUpdate();
    }
  }

  public getBatteryStateOfHealth(): number {
    return this.ECarSettings?.batteryStateOfHealth ?? 100;
  }

  public setBatteryStateOfHealth(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.batteryStateOfHealth = value;
      this.ecarSettingsUpdate();
    }
  }

  public getFastChargeLimit(): number | undefined {
    return this.ECarSettings?.fastChargeLimit;
  }

  public setFastChargeLimit(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.fastChargeLimit = value;
      this.ecarSettingsUpdate();
    }
  }

  public getTripRecording(): number {
    return this.ECarSettings?.tripRecording ?? this.ObservableTripRecordingChanged.value;
  }

  public setTripRecording(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.tripRecording = value;
      this.ecarSettingsUpdate();
    }
    this.ObservableTripRecordingChanged.next(value);
  }

  public setSliderRangeInputParams(params: any): void {
    if (this.ECarSettings?.batteryStateOfHealth) {
      params.batteryStateOfHealth = this.ECarSettings.batteryStateOfHealth;
    }
    if (this.ECarSettings?.fastChargeLimit) {
      params.fastChargeLimit = this.ECarSettings.fastChargeLimit;
    }
    this.ECarSettings = params;
    this.ecarSettingsUpdate();
  }

  public loadCarSettingsCookies(): void {
    let batteryStateOfHealth, fastChargeLimit;
    try {
      const batteryData = localStorage.getItem('batteryStateOfHealth');
      batteryStateOfHealth = batteryData ? JSON.parse(batteryData) : null;
    } catch (e) {}
    if (batteryStateOfHealth != null) {
      this.setBatteryStateOfHealth(batteryStateOfHealth);
    }
    try {
      const chargeData = localStorage.getItem('fastChargeLimit');
      fastChargeLimit = chargeData ? JSON.parse(chargeData) : null;
    } catch (e) {}
    if (fastChargeLimit != null) {
      this.setFastChargeLimit(fastChargeLimit);
    }
    let tripRecording;
    try {
      const tripRecordingData = localStorage.getItem('tripRecording');
      tripRecording = tripRecordingData ? JSON.parse(tripRecordingData) : null;
    } catch (e) {}
    if (tripRecording != null) {
      this.setTripRecording(tripRecording);
    }
  }

  public initWebViewSetBattery(): void {
    this.webviewService.InitSetBattery((battery) => {
      this.setBattery(battery);
      this.ecarSettingsUpdate();
    });
  }

  public ecarSettingsUpdate(): void {
    this.ObservableECarSettings.next(this.ECarSettings ? { ...this.ECarSettings } : null);
  }
}
